'use client';

import React, { useEffect, useState } from 'react';

const costData = [
  {
    city: 'Berlin', country: 'Germany', flag: '🇩🇪',
    items: [
      { label: 'Rent (shared)', eur: 620, coins: 6 },
      { label: 'Groceries', eur: 180, coins: 2 },
      { label: 'Transport', eur: 29, coins: 1 },
      { label: 'University fees', eur: 320, coins: 3 },
      { label: 'Misc / social', eur: 140, coins: 1 },
    ],
    total: 1289,
    note: 'Semester fee includes transit pass',
  },
  {
    city: 'Seoul', country: 'South Korea', flag: '🇰🇷',
    items: [
      { label: 'Dorm (on-campus)', eur: 380, coins: 4 },
      { label: 'Groceries', eur: 160, coins: 2 },
      { label: 'Transport', eur: 45, coins: 1 },
      { label: 'Tuition (intl.)', eur: 890, coins: 9 },
      { label: 'Misc / social', eur: 200, coins: 2 },
    ],
    total: 1675,
    note: 'Converted from KRW at Feb 2026 rate',
  },
  {
    city: 'Amsterdam', country: 'Netherlands', flag: '🇳🇱',
    items: [
      { label: 'Rent (private)', eur: 980, coins: 10 },
      { label: 'Groceries', eur: 200, coins: 2 },
      { label: 'Transport', eur: 40, coins: 1 },
      { label: 'Tuition (EU rate)', eur: 210, coins: 2 },
      { label: 'Misc / social', eur: 160, coins: 2 },
    ],
    total: 1590,
    note: 'Rent is the primary variable — plan early',
  },
];

export default function CostPanel() {
  const [animated, setAnimated] = useState(false);
  const [activeCity, setActiveCity] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const city = costData?.[activeCity];
  const maxCost = Math.max(...costData?.map(c => c?.total));

  return (
    <section className="scroll-panel" style={{ background: '#F2EDE3' }}>
      <div className="noise-overlay" />
      <div className="w-full h-full flex flex-col md:flex-row items-stretch pt-16 pb-12">

        {/* LEFT */}
        <div
          className="flex-shrink-0 md:w-[40%] flex flex-col justify-between px-8 md:px-12 py-8 md:py-10"
          style={{ borderRight: '1px solid rgba(184,175,166,0.4)' }}
        >
          <div className="panel-content">
            <div className="section-label mb-4">Chapter 02 — Money</div>
            <hr className="tatami-rule mb-8" />

            <h2
              className="font-fraunces font-medium mb-6"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.8rem)', color: 'var(--sumi)', letterSpacing: '-0.02em', lineHeight: 1.1 }}
            >
              The gap between<br />
              <span style={{ fontStyle: 'italic' }}>the estimate</span><br />
              and the reality.
            </h2>

            <p className="font-dm-sans mb-6" style={{ fontSize: '14px', color: 'var(--sumi-light)', lineHeight: 1.7, fontWeight: 300, maxWidth: '300px' }}>
              Universities quote €800/month. Students report €1,400. The difference lives in deposits, SIM cards, kitchen supplies, and the first week before your bank transfer clears.
            </p>

            {/* City selector */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
              {costData?.map((c, i) => (
                <button
                  key={c?.city}
                  onClick={() => setActiveCity(i)}
                  className="font-dm-sans text-left transition-all duration-200"
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: activeCity === i ? 600 : 400,
                    color: activeCity === i ? 'var(--washi)' : 'var(--sumi)',
                    background: activeCity === i ? 'var(--sumi)' : 'transparent',
                    border: '1px solid',
                    borderColor: activeCity === i ? 'var(--sumi)' : 'rgba(184,175,166,0.4)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <span style={{ fontSize: '16px' }}>{c?.flag}</span>
                  {c?.city}
                  <span className="font-plex-mono" style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.7 }}>
                    €{c?.total?.toLocaleString()}/mo
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="section-label mt-4" style={{ color: 'var(--tatami)' }}>
            All figures in EUR · Real student data 2024–25
          </div>
        </div>

        {/* RIGHT: Cost breakdown */}
        <div className="flex-1 flex flex-col justify-center px-8 md:px-12 py-8 md:py-10">
          <div className="panel-content">
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="section-label mb-1">{city?.flag} {city?.city}, {city?.country}</div>
                <div
                  className="font-fraunces"
                  style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 600, color: 'var(--sumi)', letterSpacing: '-0.03em', lineHeight: 1 }}
                >
                  €{city?.total?.toLocaleString()}
                </div>
                <div className="section-label mt-1" style={{ color: 'var(--tatami)' }}>estimated monthly total</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="section-label mb-1">vs. uni estimate</div>
                <div className="font-fraunces" style={{ fontSize: '28px', fontWeight: 600, color: 'var(--vermillion)', letterSpacing: '-0.02em' }}>
                  +{Math.round(((city?.total - 800) / 800) * 100)}%
                </div>
                <div className="section-label" style={{ color: 'var(--vermillion)' }}>above brochure</div>
              </div>
            </div>

            {/* Stacked coin visualization */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {city?.items?.map((item, i) => (
                <div key={item?.label} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Coin stack */}
                  <div style={{ display: 'flex', gap: '3px', width: '80px', justifyContent: 'flex-start' }}>
                    {Array.from({ length: item?.coins })?.map((_, ci) => (
                      <div
                        key={ci}
                        className="coin-stack-item"
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: i === 0 ? 'var(--vermillion)' : 'var(--sumi)',
                          opacity: animated ? 0.8 : 0,
                          animationDelay: `${i * 0.1 + ci * 0.04}s`,
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </div>
                  {/* Label */}
                  <span className="font-dm-sans" style={{ fontSize: '13px', color: 'var(--sumi)', flex: 1, fontWeight: 400 }}>
                    {item?.label}
                  </span>
                  {/* Amount */}
                  <span className="font-plex-mono" style={{ fontSize: '13px', color: 'var(--sumi)', fontWeight: 500 }}>
                    €{item?.eur}
                  </span>
                  {/* Bar */}
                  <div style={{ width: '120px', height: '4px', background: 'rgba(184,175,166,0.3)' }}>
                    <div style={{
                      height: '100%',
                      background: i === 0 ? 'var(--vermillion)' : 'var(--sumi)',
                      width: animated ? `${(item?.eur / city?.total) * 100}%` : '0%',
                      transition: `width 0.8s cubic-bezier(0.19, 1, 0.22, 1) ${i * 0.1}s`,
                      opacity: 0.7,
                    }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '16px', padding: '10px 14px', background: 'rgba(209,75,61,0.06)', borderLeft: '2px solid var(--vermillion)' }}>
              <p className="font-plex-mono" style={{ fontSize: '10px', color: 'var(--sumi-light)', letterSpacing: '0.05em' }}>
                ※ {city?.note}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}